import { Flex, Button, Menu } from '@mantine/core';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Icon from '../../../components/Icon';
import styles from '../ModelDetail.module.scss';
import { GetAiModelDetailResponseData } from '@manifest-cyber/types/interface/contracts/model-analysis/get.contract';
import appConfig from '../../../configs/appConfig';
import { captureExceptionWithMessage } from '../../../lib/sentry/captureExceptionWithMessage/captureExceptionWithMessage';
import { AxiosProxy } from '../../../api/axiosProxy/axiosProxy';
import { useNotifications } from '../../../hooks/utils/useNotifications';
import { useNavigate } from 'react-router-dom';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';

interface ModelHeaderProps {
  modelData: GetAiModelDetailResponseData;
}

export const ModelHeader = ({ modelData }: ModelHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccessNotification, error: showErrorNotification } =
    useNotifications();  // Check user permissions
  const canManageInventory = useFetchUserHasPermissions({
    requiredPermissions: ['update:ai-model-inventory'],
  });

  const canCreateRequests = useFetchUserHasPermissions({
    requiredPermissions: ['create:ai-model-request'],
  });

  // 1️⃣ isInInventory is driven directly from the server cache (no local replica)
  const isInInventory = modelData.isInInventory;

  // Simple mutation without optimistic updates
  const { mutate: toggleInventory, isLoading: isToggling } = useMutation(
    // mutationFn
    async (nextInInventory: boolean) => {
      if (nextInInventory) {
        return AxiosProxy.put({
          url: `model-analysis/inventory/${modelData._id}`,
          body: {},
        });
      } else {
        return AxiosProxy.delete({
          url: `model-analysis/inventory/${modelData._id}`,
        });
      }
    },
    {
      // On success: show notification and refetch data
      onSuccess: (_data, nextInInventory) => {
        showSuccessNotification({
          title: t(
            `mosaic.modelDetail.notification.${nextInInventory ? 'modelAddedToInventory' : 'modelRemovedFromInventory'
            }`,
          ),
          message: (
            <Button onClick={() => navigate('/models')} size="sm" variant="default">
              {t('mosaic.modelDetail.notification.seeAllModels')}
            </Button>
          ),
        });

        // Refetch to get latest data from server
        queryClient.invalidateQueries({
          queryKey: ['modelDetail', modelData._id],
        });
        queryClient.invalidateQueries({
          queryKey: ['ai-models'],
        });
      },
      // On error: show error notification (button will re-enable automatically)
      onError: (err) => {
        captureExceptionWithMessage('Failed to update inventory status', err);
        showErrorNotification({
          title: t('mosaic.modelDetail.notification.errorUpdatingInventory'),
        });
      },
    },
  );

  // Mutation for creating model requests
  const { mutate: createModelRequest, isLoading: isCreatingRequest } = useMutation(
    async () => {
      return AxiosProxy.put({
        url: `model-analysis/inventory/${modelData._id}/request`,
        body: {},
      });
    },
    {
      onSuccess: () => {
        showSuccessNotification({
          title: t('mosaic.modelDetail.notification.modelRequestCreated'),
          message: (
            <Button onClick={() => navigate('/models')} size="sm" variant="default">
              {t('mosaic.modelDetail.notification.seeAllModels')}
            </Button>
          ),
        });

        // Refetch to get latest data from server
        queryClient.invalidateQueries({
          queryKey: ['modelDetail', modelData._id],
        });
        queryClient.invalidateQueries({
          queryKey: ['ai-models'],
        });
      },
      onError: (err) => {
        captureExceptionWithMessage('Failed to create model request', err);
        showErrorNotification({
          title: t('mosaic.modelDetail.notification.errorCreatingRequest'),
        });
      },
    },
  );

  const handleInventoryToggle = () => {
    toggleInventory(!isInInventory);
  };

  const handleRequestApproval = () => {
    createModelRequest();
  };

  const handleDownloadRequest = async (type: 'bom' | 'report') => {
    try {
      const params = new URLSearchParams({
        modelId: modelData._id.toString(),
        downloadType: type,
      });
      if (type === 'report') params.append('downloadFormat', 'docx');

      window.open(
        `//${appConfig.apiUrl}/v${appConfig.apiVersion}/model-analysis/download?${params.toString()}`,
        '_blank',
      );
    } catch (e) {
      captureExceptionWithMessage(`Sboms handleDownloadRequest:`, e);
      showErrorNotification({
        title: t('mosaic.modelDetail.notification.errorDownloading'),
      });
    }
  };

  const downloadReportLink = `//${appConfig.apiUrl}/v${appConfig.apiVersion}/model-analysis/download?modelId=${modelData._id}&downloadType=report&downloadFormat=docx`;

  return (
    <Flex justify="space-between" align="center" mb="md">
      <Flex direction="column" gap={8}>
        <TextComponent variant="headerAllCaps" color="primary">
          {modelData.supplier || t('global.unknown')}
        </TextComponent>
        <TextComponent variant="headerXLarge">{modelData.name}</TextComponent>
        <Flex direction="row" gap={12}>
          <Flex direction="row" align="center" gap={4}>
            <Icon icon="grid-2" classNames={styles.headerIcon} />
            <TextComponent variant="componentTextEntry">
              {modelData.type || t('global.unknown')}
            </TextComponent>
          </Flex>
          <Flex direction="row" align="center" gap={4}>
            <Icon icon="key" classNames={styles.headerIcon} />
            <TextComponent variant="componentTextEntry">
              {modelData.license || t('global.unknown')}
            </TextComponent>
          </Flex>
          <Flex direction="row" align="center" gap={4}>
            <Icon icon="rotate-reverse" classNames={styles.headerIcon} />
            <TextComponent variant="componentTextEntry">
              {modelData.dateCreated
                ? formatCreationDate(modelData.dateCreated.toString(), t)
                : t('global.unknown')}
            </TextComponent>
          </Flex>
        </Flex>      </Flex>
      <Flex gap="md">
        {(canManageInventory) ? (
          <Button
            leftIcon={<Icon icon={isInInventory ? 'minus' : 'plus'} />}
            onClick={handleInventoryToggle}
            loading={isToggling}
            variant={isInInventory ? 'default' : 'filled'}
            disabled={isToggling}
          >
            {isInInventory
              ? t('mosaic.modelDetail.headers.removeFromInventory')
              : t('mosaic.modelDetail.headers.addToInventory')}
          </Button>
        ) : canCreateRequests ? (
          <Button
            leftIcon={<Icon icon="plus" />}
            onClick={handleRequestApproval}
            loading={isCreatingRequest}
            variant="filled"
            disabled={isCreatingRequest}
          >
            {t('mosaic.modelDetail.headers.requestApproval')}
          </Button>
        ) : null}
        <Menu withinPortal position="bottom-end" shadow="sm">
          <Menu.Target>
            <Button
              variant="default"
              leftIcon={
                <Icon icon="arrow-down-to-bracket" classNames={styles.downloadIcon} />
              }
              rightIcon={<Icon icon="chevron-down" classNames={styles.downloadIcon} />}
            >
              {t('mosaic.modelDetail.headers.download')}
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              icon={
                <Icon icon="arrow-down-to-bracket" classNames={styles.downloadIcon} />
              }
              onClick={() => handleDownloadRequest('bom')}
            >
              {t('mosaic.modelDetail.headers.downloadOptions.downloadBom')}
            </Menu.Item>
            <Menu.Item
              icon={
                <Icon icon="arrow-down-to-bracket" classNames={styles.downloadIcon} />
              }
              onClick={() => handleDownloadRequest('report')}
            >
              {t('mosaic.modelDetail.headers.downloadOptions.downloadReport')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    </Flex>
  );
};

function formatCreationDate(dateString: string, t: any): string {
  const date = new Date(dateString);
  return !isNaN(date.getTime())
    ? `${t('mosaic.modelDetail.modelInfo.generatedOn')} ${date.toLocaleDateString(
      'en-US',
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      },
    )}`
    : t('global.unknown');
}
